import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { round } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { filter, take } from 'rxjs/operators';
import { DebouncedEventTracker } from '../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../../cross-team-shared/event-tracking/event-utils';
import {
  assertStore,
  EventTrackingValueWithSource,
} from '../../../cross-team-shared/misc-utils';
import { settingsStoreToken } from '../../../cross-team-shared/state/setting-state';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { CameraState } from '../../state/camera-state';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { mainViewRegionName } from '../../state/view-region-state';
import { KdTreeService } from '../../utils/kd-tree.service';
import { pointToVector, vectorToPoint } from '../../utils/point-cloud-utils';
import {
  addPoints,
  distanceBetween,
  subtractPoints,
} from '../../utils/point-utils';
import { MapControls } from '../controls/OrbitControls';
import { PointCloudService } from '../mesh/cloud/point-cloud.service';
import { RendererLayer, setRendererLayers } from '../render-utils';
import {
  getPositionFromBoundingSphere,
  getPositionWithSensor,
} from './reset-camera-helper';
import { CameraType, SwitchableCamera } from './switchable-camera';

export type CameraOperationType =
  | 'rotate'
  | 'pan'
  | 'dolly'
  | 'inspect'
  | 'reset';

@Injectable()
export class MainCameraService extends InjectableSuperclass {
  camera = new SwitchableCamera();
  controls!: any; // should be `MapControls`, but we don't get good typing
  mainDomElement!: HTMLElement;

  private cameraEventTracker!: DebouncedEventTracker;
  private lastCameraStateBeforeOrthographic!: CameraState;
  private accumulatedDollyDistanceInOrthographic = 1;

  private kdTreeService = inject(KdTreeService);
  private pointCloudService = inject(PointCloudService);
  private shellConnector = inject(shellConnectorToken);
  private store = inject(PointCloudContentStore);
  private settingsStore = inject(settingsStoreToken);

  constructor() {
    super();

    setRendererLayers([RendererLayer.Main], this.camera);
    this.camera.up.set(0, 0, 1);
    this.cameraEventTracker = new DebouncedEventTracker(
      { id: 'move_3d_camera', bucket: 'navigation', sub_bucket: 'frame' },
      buildTrackerFromShellConnector(this.shellConnector),
    );
    this.subscribeTo(
      this.settingsStore('useOrthographicCamera').$.pipe(
        filter(() => isDefined(this.controls)),
      ),
      (isOrthographicCamera) => this.setType(isOrthographicCamera),
    );
  }

  // This is separate from the constructor so we can call it later,
  // after the scene controller's DOM element is available
  install(domElement: HTMLElement) {
    this.mainDomElement = domElement;

    // @ts-ignore
    this.controls = new MapControls(this.camera);
    this.controls.domElement = this.mainDomElement;
    this.setSubscriptions();
  }

  reset(source?: EventTrackingValueWithSource) {
    const type = this.camera.cameraType;
    const { target, position } = this.getCameraTargetAndPosition(type);

    if (source) {
      this.updateCameraAndTrack('reset', { type, position, target }, source);
    } else {
      this.store('camera').set({ type, position, target });
    }
  }

  onDragOrMouseUp(operation: CameraOperationType) {
    this.updateCameraAndTrack(operation, {
      position: vectorToPoint(this.camera.position),
      target: vectorToPoint(this.controls.target),
    });
  }

  updateCameraAndTrack(
    cameraOperation: CameraOperationType,
    newCameraState: Partial<CameraState>,
    source?: EventTrackingValueWithSource,
  ) {
    if (!this.cameraEventTracker.isDebouncePending()) {
      this.cameraEventTracker.addBatchContext({
        distance_to_target_start: round(
          distanceBetween(
            this.store.state().camera.position,
            this.store.state().camera.target,
          ),
          2,
        ),
      });
    }

    this.store('camera').assign(newCameraState);

    this.cameraEventTracker.addBatchContext({
      [cameraOperation]: true,
    });
    const { position, target, type } = this.store.state().camera;
    this.cameraEventTracker.trackWithDebounce({
      ...source,
      camera_type: type,
      distance_to_target_end: round(distanceBetween(position, target), 2),
    });
  }

  updateAccumulatedDollyDistance(dollyDistance: number) {
    if (this.store.state().camera.type === 'orthographic') {
      this.accumulatedDollyDistanceInOrthographic *= dollyDistance;
    }
  }

  private setSubscriptions() {
    this.subscribeTo(
      this.store('camera').$.pipe(filter(isDefined)),
      this.updateCameraFromState,
    );

    const frameNumber = this.store.state().render.frameNumber;
    this.subscribeTo(
      this.store('sensorRendererState')('sensorFrames')(frameNumber).$.pipe(
        filter(isDefined),
        take(1),
      ),
      () => this.reset(),
    );
    const mainViewRegionStore =
      this.store('viewRegions')('regions')(mainViewRegionName);
    assertStore(mainViewRegionStore, 'main view region must exist');

    this.subscribeTo(mainViewRegionStore.$, ({ size }) => {
      this.camera.aspect = size[0] / size[1];
      this.camera.updateProjectionMatrix();
    });
  }

  private updateCameraFromState(camera: CameraState) {
    const { position, target, type } = camera;
    this.camera.cameraType = type;
    this.camera.position.fromArray(position);
    this.camera.targetDistance = distanceBetween(position, target);
    this.camera.updateProjectionMatrix();
    this.controls.target.fromArray(target);
    this.controls.update();
  }

  private orbitToPerspectiveView() {
    const target = pointToVector(this.store.state().camera.target);

    const lastTargetDifference = subtractPoints(
      vectorToPoint(target),
      this.lastCameraStateBeforeOrthographic.target,
    );

    const newPosition = addPoints(
      this.lastCameraStateBeforeOrthographic.position,
      lastTargetDifference,
    );

    const newPositionWithDolly = vectorToPoint(
      pointToVector(newPosition)
        .sub(target)
        .multiplyScalar(this.accumulatedDollyDistanceInOrthographic)
        .add(target),
    );

    this.accumulatedDollyDistanceInOrthographic = 1;

    this.store('camera')('position').set(newPositionWithDolly);
  }

  private setType(isOrthographicCamera: boolean) {
    // limits the camera's rotation on the XY axis when orthographic
    this.controls.maxPolarAngle = isOrthographicCamera ? 0 : Math.PI;

    if (isOrthographicCamera) {
      this.lastCameraStateBeforeOrthographic = this.store.state().camera;
      this.orbitToTopDownView();
    } else {
      this.orbitToPerspectiveView();
    }

    this.store('camera')('type').set(
      isOrthographicCamera ? 'orthographic' : 'perspective',
    );
  }

  private orbitToTopDownView() {
    // We actually orbit to _almost_ a top-down view. If we went perfectly
    // top-down, the camera would point parallel to its "up" vector and be
    // rotated in an unnatural way. Instead we keep the camera slightly offset
    // in the same direction it currently is.
    const { position, target } = this.store.state().camera;
    const offset = pointToVector(subtractPoints(position, target));
    offset.z = 0;
    offset.normalize().multiplyScalar(1e-5);
    offset.z = distanceBetween(position, target);
    this.store('camera')('position').set(
      addPoints(target, vectorToPoint(offset)),
    );
  }

  private getCameraTargetAndPosition(type: CameraType) {
    const locations = this.store.state().locations;
    const frameNumber = this.store.state().render.frameNumber;

    if (locations) {
      return {
        position: getPositionWithSensor(locations[frameNumber]),
        target: locations[frameNumber].position,
      };
    } else {
      const cloud = this.pointCloudService.getCurrentCloud();
      assert(cloud, 'No point cloud data available for reset');
      cloud.cloud.geometry.computeBoundingSphere();
      const target = vectorToPoint(cloud.cloud.geometry.boundingSphere!.center);
      target[2] = this.kdTreeService.getGroundZ([target[0], target[1]]);
      return {
        position: getPositionFromBoundingSphere(type, target),
        target,
      };
    }
  }
}
